import { onHttpRequestError } from "./onHttpRequestError"

const BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? ""
const DEFAULT_TIMEOUT_MS = 10_000

// ---------------------------------------------------------------------------
// Auth helpers
// ---------------------------------------------------------------------------

export const getCustomerToken = (): string | null => {
  try {
    const token = localStorage.getItem("auth_token")
    return token ? `Bearer ${token}` : null
  } catch {
    return null
  }
}

const withCustomerAuth = (headers: HeadersInit = {}): HeadersInit => {
  const token = getCustomerToken()
  return token ? { ...headers, Authorization: token } : headers
}

// ---------------------------------------------------------------------------
// Query string builder – hỗ trợ nested object theo Strapi bracket notation
// Ví dụ: { filters: { slug: { $eq: "abc" } } }
//      → filters[slug][$eq]=abc
// ---------------------------------------------------------------------------

function buildQueryString(
  obj: Record<string, any>,
  parentKey = "",
  parts: string[] = [],
): string {
  for (const [key, value] of Object.entries(obj)) {
    const fullKey = parentKey ? `${parentKey}[${key}]` : key

    if (value === null || value === undefined) continue

    if (Array.isArray(value)) {
      value.forEach((item, index) => {
        if (typeof item === "object" && item !== null) {
          buildQueryString(item, `${fullKey}[${index}]`, parts)
        } else {
          parts.push(`${encodeURIComponent(`${fullKey}[${index}]`)}=${encodeURIComponent(String(item))}`)
        }
      })
    } else if (typeof value === "object") {
      buildQueryString(value, fullKey, parts)
    } else {
      parts.push(`${encodeURIComponent(fullKey)}=${encodeURIComponent(String(value))}`)
    }
  }
  return parts.join("&")
}

// ---------------------------------------------------------------------------
// AbortSignal timeout helper
// ---------------------------------------------------------------------------

const timeoutSignal = (ms: number): AbortSignal => {
  const controller = new AbortController()
  setTimeout(() => controller.abort(), ms)
  return controller.signal
}

// ---------------------------------------------------------------------------
// Next.js cache options builder
// ---------------------------------------------------------------------------

const buildNextOptions = (
  revalidate?: number | false,
  tags?: string[],
): { next?: NextFetchRequestConfig } => {
  if (revalidate === undefined && !tags?.length) return {}
  const next: NextFetchRequestConfig = {}
  if (revalidate !== undefined) next.revalidate = revalidate
  if (tags?.length) next.tags = tags
  return { next }
}

// ---------------------------------------------------------------------------
// Core fetch
// ---------------------------------------------------------------------------

interface RequestOptions extends RequestInit {
  apiName?: string
  revalidate?: number | false
  tags?: string[]
  /** Query params – hỗ trợ nested object, tương đương axios `params` */
  params?: Record<string, any>
  /**
   * Opt out of the automatic `filters[domain][$eq]=<current-site>` injection
   * when called from admin client code. Server-side / public-site usage is
   * never affected (no store on the server).
   */
  skipDomainFilter?: boolean
}

// Admin domain-filter injection — client-side only, no-op on server.
// Lazy require avoids hoisting the Zustand store into the server bundle.
const injectAdminDomainFilter = (params: Record<string, any> | undefined, skip?: boolean): Record<string, any> | undefined => {
  return params;

  // if (skip) return params
  // if (typeof window === "undefined") return params
  // try {
  //   // eslint-disable-next-line @typescript-eslint/no-var-requires
  //   const { useDomainStore } = require("@/stores/use-domain-store")
  //   const selected = useDomainStore.getState().selectedDomain
  //   const domain = selected?.attributes?.domain
  //   if (!domain || selected?.id === 0) return params
  //   const next = { ...(params || {}) }
  //   if (!next["filters[domain][$eq]"] && !next?.filters?.domain) {
  //     next["filters[domain][$eq]"] = domain
  //   }
  //   return next
  // } catch {
  //   return params
  // }
}

async function coreRequest<T>(
  method: string,
  url: string,
  {
    apiName,
    revalidate,
    tags,
    headers: extraHeaders,
    body,
    signal,
    params,
    skipDomainFilter,
    ...rest
  }: RequestOptions = {},
): Promise<T | null> {
  // Build URL với query string
  let fullUrl = url.startsWith("http") ? url : `${BASE_URL}${url}`

  const effectiveParams = injectAdminDomainFilter(params, skipDomainFilter)

  if (effectiveParams && Object.keys(effectiveParams).length > 0) {
    const qs = buildQueryString(effectiveParams)
    if (qs) fullUrl += (fullUrl.includes("?") ? "&" : "?") + qs
  }

  const headers: HeadersInit = withCustomerAuth({
    "Content-Type": "application/json",
    Connection: "keep-alive",
    ...(extraHeaders as object),
  })

  const options: RequestInit & { next?: NextFetchRequestConfig } = {
    method,
    headers,
    body,
    signal: signal ?? timeoutSignal(DEFAULT_TIMEOUT_MS),
    ...buildNextOptions(revalidate, tags),
    ...rest,
  }

  try {
    const response = await fetch(fullUrl, options)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      const syntheticResponse = {
        status: response.status,
        statusText: response.statusText,
        data: {
          data: null,
          error: errorData.error ?? errorData.message ?? null,
        },
        message: errorData.message ?? errorData.error ?? response.statusText,
      }
      onHttpRequestError(syntheticResponse, apiName)
      return null
    }

    const text = await response.text()
    return text ? (JSON.parse(text) as T) : null
  } catch (error) {
    const syntheticResponse = {
      data: { data: null, error: null },
      message: error instanceof Error ? error.message : String(error),
    }
    onHttpRequestError(syntheticResponse, apiName)
    return null
  }
}

// ---------------------------------------------------------------------------
// ConfigsProps – giữ nguyên shape cũ
// ---------------------------------------------------------------------------

export interface ConfigsProps extends Omit<RequestOptions, "body" | "method"> { }

// ---------------------------------------------------------------------------
// Public API – giữ nguyên tên & signature
// ---------------------------------------------------------------------------

export const getRequest = <T = any>(
  url: string,
  configs: ConfigsProps = {},
): Promise<T | null> => coreRequest<T>("GET", url, configs)

export const postRequest = <T = any>(
  url: string,
  data?: any,
  configs: ConfigsProps = {},
): Promise<T | null> =>
  coreRequest<T>("POST", url, {
    ...configs,
    revalidate: configs.revalidate ?? 0,
    body: data !== undefined ? JSON.stringify(data) : undefined,
  })

export const putRequest = <T = any>(
  url: string,
  data?: any,
  configs: ConfigsProps = {},
): Promise<T | null> =>
  coreRequest<T>("PUT", url, {
    ...configs,
    revalidate: configs.revalidate ?? 0,
    body: data !== undefined ? JSON.stringify(data) : undefined,
  })

export const patchRequest = <T = any>(
  url: string,
  data?: any,
  configs: ConfigsProps = {},
): Promise<T | null> =>
  coreRequest<T>("PATCH", url, {
    ...configs,
    revalidate: configs.revalidate ?? 0,
    body: data !== undefined ? JSON.stringify(data) : undefined,
  })

export const deleteRequest = <T = any>(
  url: string,
  configs: ConfigsProps = {},
): Promise<T | null> =>
  coreRequest<T>("DELETE", url, {
    ...configs,
    revalidate: configs.revalidate ?? 0,
  })

// ---------------------------------------------------------------------------
// downloadFile
// ---------------------------------------------------------------------------

export const downloadFile = async (url: string, filename?: string): Promise<void> => {
  const fullUrl = url.startsWith("http") ? url : `${BASE_URL}${url}`

  try {
    const response = await fetch(fullUrl, {
      method: "GET",
      headers: withCustomerAuth(),
      signal: timeoutSignal(30_000),
    })

    if (!response.ok) {
      throw new Error(`Download failed: ${response.status} ${response.statusText}`)
    }

    const blob = await response.blob()
    const contentType = response.headers.get("content-type") ?? "text/csv"
    const typedBlob = new Blob([blob], { type: contentType })
    const blobUrl = window.URL.createObjectURL(typedBlob)

    const a = document.createElement("a")
    a.href = blobUrl

    let finalFilename = filename
    const contentDisposition = response.headers.get("content-disposition") ?? ""

    if (!finalFilename && contentDisposition) {
      const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
      if (match?.[1]) finalFilename = match[1].replace(/['"]/g, "")
    }

    const inferExt = (ct: string): string => {
      if (ct.includes("spreadsheetml") || ct.includes("xlsx")) return ".xlsx"
      if (ct.includes("xls")) return ".xls"
      return ".csv"
    }
    const ext = inferExt(contentType)
    if (!finalFilename) {
      finalFilename = `export-${new Date().toISOString().split("T")[0]}${ext}`
    } else if (!finalFilename.match(/\.\w+$/)) {
      finalFilename = `${finalFilename}${ext}`
    }

    a.download = finalFilename
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(blobUrl)
    document.body.removeChild(a)
  } catch (error) {
    const syntheticResponse = {
      data: { data: null, error: null },
      message: error instanceof Error ? error.message : String(error),
    }
    onHttpRequestError(syntheticResponse, "downloadFile")
    throw error
  }
}

// ---------------------------------------------------------------------------
// HttpClient static class – giữ nguyên 100%
// ---------------------------------------------------------------------------

export class HttpClient {
  static get = getRequest
  static post = postRequest
  static put = putRequest
  static patch = patchRequest
  static delete = deleteRequest
  static downloadFile = downloadFile
}